import { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, AlertTriangle, Search, ChevronDown, ChevronRight, Wallet, CalendarDays } from 'lucide-react';
import { crmStore, useCrm } from '../store';
import { Card, PageTitle } from '../ui';
import { formatEUR, formatDateFR } from '../utils';
import type { Facture } from '../types';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmDialog';
import { useStructureFilter } from '../structureContext';
import { StructureBadge } from '../components/StructureSelector';
import { FactureBlock, getEcheances, type EchRow } from '../components/EcheancierEditor';
import { fetchEcheancierFromProp } from '../utils/echeancierFromProp';

const MONTHS_FR = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
function monthLabel(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}


export default function SuiviReglement() {
  const data = useCrm();
  const confirm = useConfirm();
  const { matches: matchesStructure } = useStructureFilter();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'enCours' | 'enRetard' | 'solde'>('enCours');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null); // facture id
  const [showMonthly, setShowMonthly] = useState(true);

  // Auto-hydratation des échéanciers depuis la propal/contrat liés.
  const hydratedRef = useRef<Set<string>>(new Set<string>());
  useEffect(() => {
    (async () => {
      for (const f of data.factures || []) {
        if (f.type !== 'Facture') continue;
        if ((f.echeancier || []).length > 0) continue;
        if (!f.prestationId) continue;
        if (['Payée', 'Annulée', 'Brouillon'].includes(f.statut)) continue;
        if (hydratedRef.current.has(f.id)) continue;
        hydratedRef.current.add(f.id);
        const prest = data.prest.find((p) => p.id === f.prestationId);
        const rows = await fetchEcheancierFromProp(f, prest?.dateEvent);
        if (rows.length > 1) {
          await crmStore.upsertFacture({
            ...f,
            echeancier: rows.map((r) => ({ label: r.label, date: r.date, montant: r.montant })),
          });
        }
      }
    })();
  }, [data.factures, data.prest]);

  // Regroupement par dossier (prestation)
  const groups = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const map = new Map<string, { prestId: string | null; client: string; numero: string; dateEvent: string; libelle: string; lieu: string; structure: any; archived: boolean; factures: Array<{ f: Facture; ech: EchRow[]; allPaid: boolean; anyLate: boolean }> }>();

    data.factures.forEach((f) => {
      if (!matchesStructure(f.structure)) return;
      if (f.type !== 'Facture') return;
      const key = f.prestationId || `__facture_${f.id}`;
      const prest = data.prest.find((p) => p.id === f.prestationId);
      if (!map.has(key)) {
        map.set(key, {
          prestId: f.prestationId,
          client: prest?.client || f.clientNom,
          numero: prest?.id || '—',
          dateEvent: prest?.dateEvent || '',
          libelle: prest?.libelle || '',
          lieu: prest?.lieu || '',
          structure: f.structure,
          archived: !!prest?.archivedAt,
          factures: [],
        });
      }
      const ech = getEcheances(f);
      const allPaid = ech.length > 0 && ech.every((e) => e.paid);
      const anyLate = ech.some((e) => !e.paid && e.date && e.date < today);
      map.get(key)!.factures.push({ f, ech, allPaid, anyLate });
    });

    let arr = Array.from(map.values());

    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((g) => g.client.toLowerCase().includes(q) || g.numero.toLowerCase().includes(q) || g.libelle.toLowerCase().includes(q) || g.lieu.toLowerCase().includes(q));
    }

    arr = arr.filter((g) => {
      const fullPaid = g.factures.every((x) => x.allPaid);
      const late = g.factures.some((x) => x.anyLate);
      if (filter === 'solde') return fullPaid;
      if (filter === 'enCours') return !fullPaid;
      if (filter === 'enRetard') return late;
      return true;
    });

    arr.sort((a, b) => {
      const aLate = a.factures.some((x) => x.anyLate) ? 0 : 1;
      const bLate = b.factures.some((x) => x.anyLate) ? 0 : 1;
      if (aLate !== bLate) return aLate - bLate;
      return (a.dateEvent || '').localeCompare(b.dateEvent || '');
    });
    return arr;
  }, [data.factures, data.prest, search, filter, matchesStructure]);

  const stats = useMemo(() => {
    let totalAttendu = 0, totalEncaisse = 0, totalRetard = 0, nbDossiersSoldes = 0, nbEnRetard = 0;
    data.factures.forEach((f) => {
      if (f.type !== 'Facture') return;
      if (!matchesStructure(f.structure)) return;
      const ech = getEcheances(f);
      ech.forEach((e) => {
        totalAttendu += e.montant;
        if (e.paid) totalEncaisse += e.montant;
        else if (e.date && e.date < new Date().toISOString().slice(0, 10)) totalRetard += e.montant;
      });
    });
    groups.forEach((g) => {
      if (g.factures.every((x) => x.allPaid)) nbDossiersSoldes++;
      if (g.factures.some((x) => x.anyLate)) nbEnRetard++;
    });
    return { totalAttendu, totalEncaisse, totalRetard, nbDossiersSoldes, nbEnRetard };
  }, [data.factures, groups, matchesStructure]);

  // Récap par mois (date d'échéance pour à venir/retard, date de paiement pour encaissé)
  const monthly = useMemo(() => {
    type M = { key: string; label: string; ts: number; encaisse: number; attendu: number; retard: number };
    const map = new Map<string, M>();
    const today = new Date().toISOString().slice(0, 10);
    const touch = (iso: string) => {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, { key, label: monthLabel(iso), ts: d.getTime(), encaisse: 0, attendu: 0, retard: 0 });
      return map.get(key)!;
    };
    data.factures.forEach((f) => {
      if (f.type !== 'Facture') return;
      if (!matchesStructure(f.structure)) return;
      getEcheances(f).forEach((e) => {
        if (e.paid && e.paid_at) {
          const m = touch(e.paid_at); if (m) m.encaisse += e.montant;
        } else if (!e.paid && e.date) {
          const m = touch(e.date);
          if (m) {
            m.attendu += e.montant;
            if (e.date < today) m.retard += e.montant;
          }
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.ts - b.ts);
  }, [data.factures, matchesStructure]);

  const toggleCollapse = (key: string) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key); else next.add(key);
    setCollapsed(next);
  };

  const persistEcheancier = async (f: Facture, newEch: EchRow[], opts?: { silent?: boolean }) => {
    const totalPaye = newEch.filter((e) => e.paid).reduce((s, e) => s + (e.montant || 0), 0);
    const allPaid = newEch.length > 0 && newEch.every((e) => e.paid);
    const anyPaid = newEch.some((e) => e.paid);
    const updated: Facture = {
      ...f,
      echeancier: newEch.map((e) => ({ label: e.label, date: e.date, montant: e.montant, paid: e.paid, paid_at: e.paid_at })),
      montantPaye: totalPaye,
      statut: allPaid ? 'Payée' : (anyPaid ? 'Partiellement payée' : (f.statut === 'Payée' || f.statut === 'Partiellement payée' ? 'Émise' : f.statut)),
      datePaiement: allPaid ? new Date().toISOString().slice(0, 10) : (f.datePaiement || ''),
    };
    await crmStore.upsertFacture(updated);
    if (!opts?.silent) toast.success('Échéancier mis à jour');
  };

  const togglePaid = async (f: Facture, ech: EchRow[], idx: number) => {
    const cur = ech[idx];
    const willPay = !cur.paid;
    const newEch: EchRow[] = ech.map((e, i) => ({
      ...e,
      paid: i === idx ? willPay : e.paid,
      paid_at: i === idx ? (willPay ? new Date().toISOString().slice(0, 10) : undefined) : e.paid_at,
    }));
    try {
      await persistEcheancier(f, newEch, { silent: true });
      toast.success(willPay ? `${cur.label} marquée payée` : `${cur.label} décochée`);
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || e}`);
    }
  };

  const archiveDossier = async (prestId: string | null, client: string) => {
    if (!prestId) return;
    const ok = await confirm({ title: 'Archiver le dossier ?', description: `Le dossier ${client} sera déplacé dans les archives.`, confirmLabel: 'Archiver' });
    if (!ok) return;
    try {
      await crmStore.archivePrest(prestId);
      toast.success('Dossier archivé');
    } catch (e: any) {
      toast.error(`Erreur : ${e?.message || e}`);
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto">
      <PageTitle icon={<Wallet size={18} strokeWidth={1.5} />} title="Suivi des règlements" subtitle="Configurez les traites, cochez chaque encaissement. Quand tout est payé, vous pouvez archiver le dossier." />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Attendu</div>
          <div className="text-xl text-slate-100 mt-1">{formatEUR(stats.totalAttendu)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400/70">Encaissé</div>
          <div className="text-xl text-emerald-300 mt-1">{formatEUR(stats.totalEncaisse)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-rose-400/70">En retard</div>
          <div className="text-xl text-rose-300 mt-1">{formatEUR(stats.totalRetard)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-400/70">Dossiers en retard</div>
          <div className="text-xl text-amber-300 mt-1">{stats.nbEnRetard}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400/70">Soldés</div>
          <div className="text-xl text-emerald-300 mt-1">{stats.nbDossiersSoldes}</div>
        </Card>
      </div>

      {/* Récap mensuel */}
      <Card className="p-4 mb-4">
        <button onClick={() => setShowMonthly((v) => !v)} className="w-full flex items-center justify-between text-left">
          <div className="flex items-center gap-2 text-sm text-slate-200">
            <CalendarDays size={14} strokeWidth={1.5} className="text-amber-400" />
            Encaissements par mois
            <span className="text-[10px] uppercase tracking-widest text-slate-500">({monthly.length} mois)</span>
          </div>
          {showMonthly ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
        </button>
        {showMonthly && (
          <div className="mt-3 overflow-x-auto">
            {monthly.length === 0 ? (
              <div className="text-xs text-slate-500 italic py-2">Aucune échéance enregistrée.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left py-2 pr-3">Mois</th>
                    <th className="text-right py-2 px-3 text-emerald-400/70">Encaissé</th>
                    <th className="text-right py-2 px-3 text-slate-400">À encaisser</th>
                    <th className="text-right py-2 pl-3 text-rose-400/70">Dont en retard</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((m) => (
                    <tr key={m.key} className="border-t border-slate-800/60">
                      <td className="py-2 pr-3 text-slate-200">{m.label}</td>
                      <td className="py-2 px-3 text-right text-emerald-300 tabular-nums">{m.encaisse > 0 ? formatEUR(m.encaisse) : '—'}</td>
                      <td className="py-2 px-3 text-right text-slate-200 tabular-nums">{m.attendu > 0 ? formatEUR(m.attendu) : '—'}</td>
                      <td className="py-2 pl-3 text-right text-rose-300 tabular-nums">{m.retard > 0 ? formatEUR(m.retard) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      {/* Filtres */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher client, dossier, lieu…"
              className="w-full pl-9 pr-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div className="flex gap-1 bg-slate-950/60 border border-slate-800 rounded-md p-1">
            {([
              ['enCours', 'En cours'],
              ['enRetard', 'En retard'],
              ['solde', 'Soldés'],
              ['all', 'Tous'],
            ] as const).map(([k, lbl]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`px-3 py-1.5 text-xs rounded transition ${filter === k ? 'bg-amber-500/20 text-amber-200' : 'text-slate-400 hover:text-slate-200'}`}
              >{lbl}</button>
            ))}
          </div>
        </div>
      </Card>

      {/* Liste */}
      <div className="space-y-3">
        {groups.length === 0 && (
          <Card className="p-12 text-center text-slate-500">Aucun dossier correspondant.</Card>
        )}
        {groups.map((g) => {
          const key = g.prestId || g.numero;
          const isCollapsed = collapsed.has(key);
          const totalDossier = g.factures.reduce((s, x) => s + x.ech.reduce((a, e) => a + e.montant, 0), 0);
          const payeDossier = g.factures.reduce((s, x) => s + x.ech.filter((e) => e.paid).reduce((a, e) => a + e.montant, 0), 0);
          const fullPaid = g.factures.every((x) => x.allPaid);
          const anyLate = g.factures.some((x) => x.anyLate);
          const pct = totalDossier > 0 ? Math.round((payeDossier / totalDossier) * 100) : 0;

          return (
            <Card key={key} className={`overflow-hidden ${anyLate && !fullPaid ? 'ring-1 ring-rose-500/30' : ''}`}>
              <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-900/40" onClick={() => toggleCollapse(key)}>
                <button className="text-slate-400">{isCollapsed ? <ChevronRight size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}</button>
                <StructureBadge code={g.structure} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-amber-300">{g.numero}</span>
                    <span className="text-slate-200 font-medium truncate">{g.client}</span>
                    {g.archived && <span className="text-[10px] uppercase tracking-widest text-slate-500">archivé</span>}
                    {fullPaid && <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 rounded">Soldé</span>}
                    {anyLate && !fullPaid && <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-rose-500/15 text-rose-300 rounded flex items-center gap-1"><AlertTriangle size={10} strokeWidth={1.5} />Retard</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {g.libelle && `${g.libelle} — `}{g.lieu && `${g.lieu} — `}{g.dateEvent && formatDateFR(g.dateEvent)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-200">{formatEUR(payeDossier)} <span className="text-slate-500">/ {formatEUR(totalDossier)}</span></div>
                  <div className="w-32 h-1.5 bg-slate-800 rounded-full mt-1 overflow-hidden">
                    <div className={`h-full ${fullPaid ? 'bg-emerald-500' : anyLate ? 'bg-rose-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                {fullPaid && g.prestId && !g.archived && (
                  <button
                    onClick={(e) => { e.stopPropagation(); archiveDossier(g.prestId, g.client); }}
                    className="ml-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 rounded-md text-xs flex items-center gap-1.5"
                  >
                    <Archive size={12} strokeWidth={1.5} />Archiver
                  </button>
                )}
              </div>

              {!isCollapsed && (
                <div className="border-t border-slate-800 bg-slate-950/30">
                  {g.factures.map(({ f, ech }) => (
                    <FactureBlock
                      key={f.id}
                      facture={f}
                      ech={ech}
                      isEditing={editing === f.id}
                      onEdit={() => setEditing(f.id)}
                      onCloseEdit={() => setEditing(null)}
                      onTogglePaid={(idx) => togglePaid(f, ech, idx)}
                      onSave={async (newEch) => {
                        try { await persistEcheancier(f, newEch); setEditing(null); }
                        catch (e: any) { toast.error(`Erreur : ${e?.message || e}`); }
                      }}
                    />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
